import { sharedToBalanceInWei } from '@shared/utils/formatter'
import { CPMMV1Vaults } from '@/app/data/vault'
import {
  VESTING_CONTRACT_CODE,
  BASIS_POINT_DECIMAL_PLACES
} from '@/app/utils/constant'
import {
  IDOProjectForm,
  CPMMConfigField,
  VaultConfigForm,
  VaultBaseConfig,
  IDOProjectField,
  VaultConfigField,
  VestingSchedule,
  CpmmPricingStrategy,
  VaultMarketMakingConfig
} from '@/types'

export const formatBaseConfig = ({
  config,
  formValues,
  contractAddress
}: {
  config: VaultBaseConfig
  contractAddress: string
  formValues: VaultConfigForm
}) => {
  return {
    owner: config.owner,
    master_address: contractAddress,
    subaccount_id: config.subaccountId,
    fee_recipient: config.feeRecipient,
    market_id: formValues[VaultConfigField.MarketId],
    order_density: Number(formValues[VaultConfigField.OrderDensity]),
    notional_value_cap: formValues[VaultConfigField.NotionalValueCap]
  }
}

export const formatMarketMarkingConfig = ({
  config,
  formValues
}: {
  formValues: VaultConfigForm
  config: VaultMarketMakingConfig
}) => {
  return {
    ...config,
    reservation_price_sensitivity_ratio:
      formValues[VaultConfigField.ReservationPriceSensitivityRatio],
    reservation_spread_sensitivity_ratio:
      formValues[VaultConfigField.ReservationSpreadSensitivityRatio],
    max_active_capital_utilization_ratio:
      formValues[VaultConfigField.MaxActiveCapitalUtilizationRatio],
    head_change_tolerance_ratio:
      formValues[VaultConfigField.HeadChangeToleranceRatio] || 0,
    head_to_tail_deviation_ratio:
      formValues[VaultConfigField.HeadToTailDeviationRatio],
    signed_min_head_to_fair_price_deviation_ratio:
      formValues[VaultConfigField.SignedMinHeadToFairPriceDeviationRatio],
    signed_min_head_to_tob_deviation_ratio:
      formValues[VaultConfigField.SignedMinHeadToTobDeviationRatio],
    default_mid_price_volatility_ratio:
      formValues[VaultConfigField.DefaultMidPriceVolatilityRatio],
    min_volatility_ratio: formValues[VaultConfigField.MinVolatilityRatio],
    min_oracle_volatility_sample_size: Number(
      formValues[VaultConfigField.MinOracleVolatilitySampleSize]
    ),
    oracle_volatility_max_age: Number(
      formValues[VaultConfigField.OracleVolatilityMaxAge]
    ),
    emergency_oracle_volatility_sample_size: Number(
      formValues[VaultConfigField.EmergencyOracleVolatilitySampleSize]
    ),
    last_valid_mark_price: formValues[VaultConfigField.LastValidMarkPrice],
    oracle_stale_time: Number(formValues[VaultConfigField.OracleStaleTime])
  }
}

export const formatMsgPricingStrategyAmount = ({
  formValues,
  contractAddress
}: {
  formValues: IDOProjectForm | VaultConfigForm
  contractAddress?: string
}): Record<string, any> => {
  const pricingStrategyType = formValues[CPMMConfigField.PricingStrategyType]

  const defaultFormat = {
    [pricingStrategyType]: formValues[CPMMConfigField.PricingStrategyAmount]
  }

  if (
    CPMMV1Vaults.includes(contractAddress || '') ||
    pricingStrategyType === CpmmPricingStrategy.ConstantPricingWithTickSize
  ) {
    return defaultFormat
  }

  return {
    [pricingStrategyType]: {
      ask_range: formValues[CPMMConfigField.PricingStrategyAskRange],
      bid_range: formValues[CPMMConfigField.PricingStrategyBidRange]
    }
  }
}

export const formatVestingSchedule = (
  formValues: IDOProjectForm
): Record<string, any> | undefined => {
  if (formValues[IDOProjectField.IsEnableVestingSchedule] !== true) {
    return
  }

  if (
    formValues[IDOProjectField.VestingScheduleType] ===
    VestingSchedule.SaturatingLinear
  ) {
    return {
      code_id: VESTING_CONTRACT_CODE,
      users_project_token: {
        schedule: VestingSchedule.SaturatingLinear,
        vesting_start_delay_seconds: Number(
          formValues[IDOProjectField.SaturatingLinearDelay]
        ),
        vesting_duration_seconds: Number(
          formValues[IDOProjectField.SaturatingLinearDuration]
        )
      }
    }
  }

  return {
    code_id: VESTING_CONTRACT_CODE,
    users_project_token: {
      vesting_duration_seconds: Number(
        formValues[IDOProjectField.PiecewiseLinearSecondVestTime]
      ),
      schedule: {
        PiecewiseLinear: [
          [1, 0],
          [
            Number(formValues[IDOProjectField.PiecewiseLinearFirstVestTime]) +
              1,
            sharedToBalanceInWei({
              value: formValues[IDOProjectField.PiecewiseLinearFirstVestAmount],
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toNumber()
          ],
          [
            Number(formValues[IDOProjectField.PiecewiseLinearSecondVestTime]),
            sharedToBalanceInWei({
              value: 100, // 100%
              decimalPlaces: BASIS_POINT_DECIMAL_PLACES
            }).toNumber()
          ]
        ]
      },
      vesting_start_delay_seconds: 0
    }
  }
}
